// ── EVIDENCE P1.2 — connect your Evidence library (onboarding) ──────────────────────────────────
//
// The admin pastes the ADDRESS of their Evidence library — never an id. Their own Microsoft session turns
// it into identifiers (VeriGRC's library-scoped token cannot), the admin confirms it is the right library
// and grants VeriGRC write on it in Microsoft 365, and VeriGRC connects.
//
// ⚠ NO SECRET IS EVER ASKED FOR. There is no password, client secret, certificate or key field anywhere in
//   this flow, and the copy says so: VeriGRC reaches the library as its own application, under a grant the
//   admin makes in Microsoft 365.
//
// ⚠ CONNECT ≠ PROVISION. A connected library holds no control folders until provisioning is run from the
//   Controls workspace or a control's Evidence tab — and the connected state says "0 controls provisioned"
//   rather than implying anything was set up.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, FolderOpen, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { CARD_SURFACE } from '@/components/compliance/card-surface';
import { ASSIGN_BTN } from '@/components/compliance/PeopleLanes';
import { apiErrorMessage } from '@/components/compliance/provisioning-copy';
import { useConnectEvidenceLibrary, useEvidenceLibraryStatus } from '@/api/hooks/useEvidenceProvisioning';
import { getDelegatedGraphClient } from '@/lib/delegated-graph';
import { resolveEvidenceLibrary, ResolveLibraryError, type ResolvedEvidenceLibrary } from '@/lib/evidence-library-resolver';

const EYEBROW = 'text-[10px] font-bold uppercase tracking-[0.12em] text-text-secondary';
const QUIET_BTN = 'rounded-[7px] border border-border-subtle px-[12px] py-[7px] text-[12.5px] text-text-secondary hover:text-text-primary';
const fmt = (iso: string | null) => iso ? new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '—';

function Step({ n, title, active, done, children }: {
  n: number; title: string; active: boolean; done: boolean; children?: React.ReactNode;
}) {
  return (
    <li className={cn('border-b border-border-subtle px-[18px] py-[14px] last:border-b-0', !active && !done && 'opacity-50')}>
      <div className="flex items-center gap-[10px]">
        <span className={cn('flex h-[20px] w-[20px] items-center justify-center rounded-full text-[11px] font-semibold',
          done ? 'bg-cert-valid-bg text-cert-valid-fg' : active ? 'bg-accent-cyan/15 text-accent-cyan' : 'bg-bg-surface-2 text-text-faint')}>
          {done ? '✓' : n}
        </span>
        <span className="text-[13px] font-semibold text-text-primary">{title}</span>
      </div>
      {active && <div className="mt-[10px] pl-[30px]">{children}</div>}
    </li>
  );
}

export function EvidenceLibraryPage() {
  const { data: status, isLoading } = useEvidenceLibraryStatus();
  const connect = useConnectEvidenceLibrary();
  const [url, setUrl] = useState('');
  const [resolving, setResolving] = useState(false);
  const [resolved, setResolved] = useState<ResolvedEvidenceLibrary | null>(null);
  const [resolveError, setResolveError] = useState<{ message: string; libraries: string[] } | null>(null);
  const [connectError, setConnectError] = useState<string | null>(null);

  const lookUp = async () => {
    setResolveError(null);
    setResolved(null);
    setResolving(true);
    try {
      const client = await getDelegatedGraphClient();
      setResolved(await resolveEvidenceLibrary(url, client));
    } catch (err) {
      setResolveError({
        message: err instanceof Error ? err.message : 'The address could not be looked up.',
        libraries: err instanceof ResolveLibraryError ? err.availableLibraries : [],
      });
    } finally {
      setResolving(false);
    }
  };

  const doConnect = () => {
    if (!resolved) return;
    setConnectError(null);
    const { siteName: _siteName, ...ids } = resolved;
    void _siteName;
    connect.mutate(ids, {
      onError: (err) => setConnectError(apiErrorMessage(err, 'The library could not be connected.')),
    });
  };

  const header = (
    <>
      <Link to="/compliance/workspace/controls" className="inline-flex items-center gap-[6px] text-[12px] text-text-secondary hover:text-text-primary">
        <ArrowLeft className="h-[12px] w-[12px]" aria-hidden="true" /> Back to controls
      </Link>
      <div>
        <span className={EYEBROW}>Evidence</span>
        <h1 className="mt-[4px] text-[20px] font-semibold text-text-primary">Evidence library</h1>
        <p className="mt-[4px] max-w-[72ch] text-[13px] text-text-secondary">
          Your evidence stays in your own SharePoint or Teams. VeriGRC connects to one library you choose and keeps track
          of which controls it holds evidence for.
        </p>
      </div>
    </>
  );

  if (isLoading || !status) {
    return <div className="space-y-[12px] p-[15px]">{header}<p className="text-[13px] text-text-faint">Checking the connection…</p></div>;
  }

  // ── CONNECTED ──
  if (status.state === 'connected') {
    return (
      <div className="space-y-[12px] p-[15px]">
        {header}
        <div className={cn(CARD_SURFACE, 'p-[18px]')} data-testid="library-connected">
          <div className="flex items-center gap-[10px]">
            <CheckCircle2 className="h-[18px] w-[18px] text-cert-valid-fg" aria-hidden="true" />
            <h2 className="text-[15px] font-semibold text-text-primary">Evidence workspace connected: {status.library_name}</h2>
          </div>
          <dl className="mt-[12px] grid gap-[8px] text-[12.5px] sm:grid-cols-[180px_1fr]">
            <dt className="text-text-secondary">Health</dt>
            <dd className="text-text-primary">Access last verified {fmt(status.validated_at)}</dd>
            {status.posture && (
              <>
                <dt className="text-text-secondary">Library protections</dt>
                <dd className="text-text-primary" data-testid="library-protections">
                  {status.posture.verified} verified · {status.posture.unverified} unverified · {status.posture.platform_default} Microsoft defaults
                </dd>
              </>
            )}
            <dt className="text-text-secondary">Provisioned controls</dt>
            <dd className="text-text-primary" data-testid="provisioned-controls">{status.provisioned_controls}</dd>
          </dl>
          {status.provisioned_controls === 0 && (
            // ⚠ CONNECT ≠ PROVISION, said plainly at the moment it would otherwise be assumed.
            <p className="mt-[12px] text-[12.5px] text-text-secondary">
              Connecting the library does not create control folders. Select controls in the{' '}
              <Link to="/compliance/workspace/controls" className="text-accent-cyan hover:underline">Controls workspace</Link> and choose
              &ldquo;Provision evidence location&rdquo; when you&apos;re ready.
            </p>
          )}
        </div>
      </div>
    );
  }

  // ── CONNECTING ──
  if (status.state === 'connecting') {
    return (
      <div className="space-y-[12px] p-[15px]">
        {header}
        <div className={cn(CARD_SURFACE, 'flex items-center gap-[10px] p-[18px]')} data-testid="library-connecting">
          <Loader2 className="h-[16px] w-[16px] animate-spin text-accent-cyan" aria-hidden="true" />
          <p className="text-[13px] text-text-primary">
            Connecting to {status.library_name} — checking that VeriGRC can read it. You can leave this page.
          </p>
        </div>
      </div>
    );
  }

  // ── NOT CONNECTED / FAILED — the wizard ──
  const step = resolved ? 2 : 1;
  return (
    <div className="space-y-[12px] p-[15px]">
      {header}

      {status.state === 'failed' && (
        <div role="alert" className="rounded-[9px] border border-cert-warn-border bg-bg-surface-2 p-[12px] text-[12.5px] text-cert-warn-fg">
          {/* ⚠ Plain words first; the technical status (e.g. a 403) stays available, not leading. */}
          <p>
            VeriGRC could not access the selected Evidence library. Confirm that the required Microsoft 365 access has
            been granted and try again. Nothing was stored.
          </p>
          {status.reason && (
            <details className="mt-[6px] text-[11.5px] text-text-secondary">
              <summary className="cursor-pointer">Technical details</summary>
              <p className="mt-[4px]">{status.reason}</p>
            </details>
          )}
        </div>
      )}

      <ol className={CARD_SURFACE}>
        <Step n={1} title="Paste your Evidence library's address" active={step === 1} done={step > 1}>
          <p className="mb-[8px] max-w-[72ch] text-[12px] text-text-secondary">
            Open the library in SharePoint (or the Files tab of a Teams channel → Open in SharePoint) and copy the address
            from your browser. It looks like <code className="text-text-primary">https://yourcompany.sharepoint.com/sites/…/Evidence</code>.
          </p>
          <div className="flex flex-wrap gap-[8px]">
            <input
              aria-label="Evidence library address"
              value={url}
              onChange={(e) => { setUrl(e.target.value); setResolveError(null); }}
              placeholder="https://yourcompany.sharepoint.com/sites/GRC/Evidence"
              className="min-w-[320px] flex-1 rounded-[7px] border border-border-subtle bg-bg-surface px-[10px] py-[7px] text-[13px] text-text-primary"
            />
            <button type="button" className={ASSIGN_BTN} disabled={!url.trim() || resolving} onClick={() => void lookUp()}>
              {resolving ? 'Looking up…' : 'Look up library'}
            </button>
          </div>
          {resolving && (
            <p className="mt-[8px] text-[12px] text-text-secondary">Looking up the Evidence library…</p>
          )}
          {resolveError && (
            <div role="alert" className="mt-[8px] text-[12px] text-cert-warn-fg">
              <p>{resolveError.message}</p>
              {resolveError.libraries.length > 0 && (
                <p className="mt-[3px] text-text-secondary">Libraries on that site: {resolveError.libraries.join(', ')}</p>
              )}
            </div>
          )}
        </Step>

        <Step n={2} title="Confirm the library and grant access" active={step === 2} done={false}>
          {resolved && (
            <>
              <div className="flex items-start gap-[10px] rounded-[8px] border border-border-subtle bg-bg-surface-2 p-[12px]" data-testid="resolved-library">
                <FolderOpen className="mt-[2px] h-[15px] w-[15px] text-text-secondary" aria-hidden="true" />
                <div className="text-[12.5px]">
                  <p className="font-semibold text-text-primary">{resolved.libraryName}</p>
                  <p className="text-text-secondary">on the {resolved.siteName} site</p>
                  <p className="mt-[2px] break-all text-text-faint">{resolved.libraryWebUrl}</p>
                </div>
              </div>
              <div className="mt-[12px] max-w-[72ch] text-[12.5px] text-text-secondary">
                <p className="font-medium text-text-primary">Before connecting, a Microsoft 365 administrator grants access:</p>
                <ol className="mt-[4px] list-decimal space-y-[3px] pl-[18px]">
                  <li>Consent to the VeriGRC application with the <em>Lists.SelectedOperations.Selected</em> permission.</li>
                  <li>Give that application <em>write</em> access to <strong>this library only</strong> — nothing else on the site.</li>
                </ol>
                <p className="mt-[8px]">
                  VeriGRC never asks for a password, client secret or certificate. It can reach this one library and nothing else.
                </p>
              </div>
              {connectError && <p role="alert" className="mt-[8px] text-[12px] text-cert-warn-fg">{connectError}</p>}
              <div className="mt-[12px] flex gap-[8px]">
                <button type="button" className={ASSIGN_BTN} disabled={connect.isPending} onClick={doConnect}>
                  Connect {resolved.libraryName}
                </button>
                <button type="button" className={QUIET_BTN} onClick={() => setResolved(null)}>Use a different library</button>
              </div>
            </>
          )}
        </Step>
      </ol>
    </div>
  );
}
